/**
 * OperatorColumn — comparison lane: input + ответ оператора + comparison result.
 */

import { useRef, useEffect } from 'react';
import type { OperatorReplyResult } from '../../api/demo';
import type { DemoRound } from './DemoExperience';

// ============================================================================
// Props
// ============================================================================

interface OperatorColumnProps {
  rounds: DemoRound[];
  currentRound: DemoRound | null;
  inputValue: string;
  onInputChange: (v: string) => void;
  onSend: () => void;
  disabled: boolean;
  isPending: boolean;
  limitReached: boolean;
}

// ============================================================================
// ComparisonResult — label first, score secondary
// ============================================================================

function ComparisonResult({ result }: { result: OperatorReplyResult }): JSX.Element {
  const { matchScore, matchLabel, matchExplanation } = result;

  let colorClass = 'text-text-secondary border-border-subtle bg-surface-subtle';
  let iconPath = 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z';

  if (matchScore > 80) {
    colorClass = 'text-accent-success border-accent-success/20 bg-accent-success-light';
    iconPath = 'M4.5 12.75l6 6 9-13.5';
  } else if (matchScore > 40) {
    colorClass = 'text-accent-ai border-accent-ai/20 bg-accent-ai-light';
    iconPath = 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z';
  }

  return (
    <div className={`rounded-card border p-3 ${colorClass}`}>
      <div className="flex items-center gap-2 mb-1">
        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
        </svg>
        <span className="text-sm font-bold">{matchLabel}</span>
        <span className="text-[10px] opacity-60 ml-auto">{matchScore}%</span>
      </div>
      <p className="text-xs opacity-80 leading-relaxed">{matchExplanation}</p>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function OperatorColumn({
  rounds,
  currentRound,
  inputValue,
  onInputChange,
  onSend,
  disabled,
  isPending,
  limitReached,
}: OperatorColumnProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevScrollKeyRef = useRef<string>('');

  const lastComparisonLabel = rounds[rounds.length - 1]?.comparisonResult?.matchLabel ?? null;
  useEffect(() => {
    const scrollKey = `${rounds.length}:${lastComparisonLabel ?? ''}`;
    if (scrollKey === prevScrollKeyRef.current) return;
    prevScrollKeyRef.current = scrollKey;
    if (rounds.length === 0 && !lastComparisonLabel) return;
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [rounds.length, lastComparisonLabel]);

  const showInput = !limitReached;
  const awaitingDraft = currentRound !== null && currentRound.proposal === null;

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-accent-success" />
          <span className="text-[10px] font-bold text-accent-success uppercase tracking-wider">Оператор</span>
        </div>
      </div>

      {/* Content */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {rounds.length === 0 && (
          <div className="flex-1 flex items-center justify-center h-32">
            <p className="text-xs text-text-muted text-center leading-relaxed">
              Ответьте как оператор<br />и сравните с AI
            </p>
          </div>
        )}

        {rounds.map(round => (
          <div key={round.clientMessage.id} className="space-y-2">
            {/* Ответ оператора */}
            {round.operatorReply && (
              <div className="flex justify-end">
                <div className="max-w-[85%] bg-accent-success/10 border border-accent-success/20 text-text-primary rounded-2xl rounded-br-sm px-3 py-2 text-sm">
                  <p className="whitespace-pre-wrap">{round.operatorReply}</p>
                </div>
              </div>
            )}

            {/* Comparison Result */}
            {round.comparisonResult && (
              <ComparisonResult result={round.comparisonResult} />
            )}

            {/* Trajectory message после comparison */}
            {round.comparisonResult && (
              <p className="text-[11px] text-text-muted text-center leading-relaxed italic">
                Это первое знакомство. С каждым ответом оператора Anima учится точнее.
              </p>
            )}
          </div>
        ))}

        {/* Подсказка если ждём оператора */}
        {currentRound && !currentRound.operatorReply && !awaitingDraft && currentRound.proposal && currentRound.proposal.status !== 'thinking' && (
          <p className="text-xs text-text-muted text-center animate-pulse">
            Теперь напишите свой ответ как оператор — и посмотрите, насколько Anima угадала
          </p>
        )}

        {/* Подсказка если ждём draft */}
        {awaitingDraft && (
          <p className="text-xs text-text-muted text-center">
            Подождите черновик AI...
          </p>
        )}

        {/* Лимит */}
        {limitReached && (
          <div className="text-center py-4 space-y-3">
            <p className="text-sm text-text-secondary font-medium">Лимит демо исчерпан</p>
            <button
              type="button"
              onClick={() => { window.location.href = '/demo'; }}
              className="inline-flex items-center px-5 py-2.5 rounded-card bg-accent-brand text-white text-sm font-medium hover:bg-accent-brand/90 transition-colors"
            >
              Хотите так же? Попробуйте бесплатно
            </button>
          </div>
        )}
      </div>

      {/* Input */}
      {showInput && (
        <div className="px-3 py-3 border-t border-border-subtle flex gap-2">
          <textarea
            value={inputValue}
            onChange={e => onInputChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={
              disabled
                ? (awaitingDraft ? 'Подождите черновик AI...' : 'Сначала отправьте вопрос клиента')
                : 'Напишите как оператор...'
            }
            disabled={disabled}
            maxLength={500}
            aria-label="Ответ оператора"
            className="flex-1 resize-none rounded-comp border border-border-subtle px-3 py-2 text-sm outline-none focus:border-accent-success bg-surface-base text-text-primary placeholder:text-text-muted disabled:opacity-50"
            rows={1}
          />
          <button
            type="button"
            onClick={onSend}
            disabled={disabled || !inputValue.trim() || isPending}
            className="px-3 py-2 rounded-comp bg-accent-success text-white text-sm font-medium hover:bg-accent-success/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Отправить"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
            </svg>
          </button>
        </div>
      )}
    </>
  );
}
